/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import {
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { formFields } from './form-fields';

/**
 * `PharmacyCampaign` type.
 */

type PharmacyCampaign = {
  id: string;
  pharmacyId: string;
  title: string;
  description: string;
  externalUrl: string | null;
  startDate: string;
  endDate: string;
  status: 'active' | 'cancelled';
  tokenCost: number;
  freeTokensUsed: number;
  createdAt: string;
  updatedAt: string;
};

/**
 * List columns.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyCampaign> => [
  {
    title: translate('common.table.columns.title'),
    dataIndex: 'title',
    key: 'title',
    size: '1fr',
    render: item => item.title
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    size: '100px',
    render: item => item.status
  },
  {
    title: translate('common.table.columns.startDate'),
    dataIndex: 'startDate',
    key: 'startDate',
    size: '130px',
    render: item => item.startDate
  },
  {
    title: translate('common.table.columns.endDate'),
    dataIndex: 'endDate',
    key: 'endDate',
    size: '130px',
    render: item => item.endDate
  },
  {
    title: translate('common.table.columns.tokenCost'),
    dataIndex: 'tokenCost',
    key: 'tokenCost',
    size: '100px',
    render: item => item.tokenCost
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '180px',
    render: item => <DateTime date={item.createdAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'status',
    inline: true,
    label: translate('common.labels.status'),
    showSearch: true,
    allowClear: true,
    options: [
      { label: translate('common.labels.active'), value: 'active' },
      { label: translate('common.labels.statusLabels.cancelled'), value: 'cancelled' }
    ]
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyCampaign> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.pharmacies.campaigns,
    canEdit: true,
    canRemove: true,
    key: ['pharmacyCampaigns'],
    resolveParams: (params: any, item: any) => ({
      pharmacyId: params?.pharmacyId,
      id: item?.id ?? params?.id
    }),
    normalizeFilters: (data: any) => ({
      filters: pickBy(data.filters, identity),
      order: [{ key: 'createdAt', direction: 'DESC' }]
    }),
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100]
    },
    endpoint: apiEndpoints.pharmacyPlan.campaigns,
    endpointById: apiEndpoints.pharmacyPlan.campaignsById
  };
}

/**
 * Remove.
 */

const remove = (): CrudRemoveType<PharmacyCampaign> => ({
  endpoint: apiEndpoints.pharmacyPlan.campaignsById,
  resolveParams: (params: any, item?: PharmacyCampaign) => ({
    id: item?.id ?? params?.id
  })
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<PharmacyCampaign> => ({
  formFields: formFields(translate),
  endpoint: apiEndpoints.pharmacyPlan.campaignsById,
  method: 'put',
  resolveParams: (params: any) => ({ id: params?.id }),
  submitLabel: translate('common.actions.edit'),
  redirect: appRoutes.dashboard.services.pharmacies.campaigns,
  normalizePayload: (values: any) => ({
    title: values.title,
    description: values.description,
    externalUrl: values.externalUrl ?? null,
    status: values.status
  })
});

/**
 * Export `createPharmacyCampaignsTemplate`.
 */

export function createPharmacyCampaignsTemplate(translate: TFunction): CrudTemplate<PharmacyCampaign> {
  return normalizeTemplate(translate, {
    list: list(translate),
    edit: edit(translate),
    remove: remove()
  });
}
